import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { RetailInventoryView } from "@/features/retail/pages/RetailInventoryPage.V3";
import KitchenInventoryPage from "@/features/inventory/pages/KitchenInventoryPage.V3";

const TAB_KEYS = ["retail", "kitchen"] as const;
type InventoryTabKey = (typeof TAB_KEYS)[number];

const resolveTabKey = (value: string | null): InventoryTabKey => {
  if (value && TAB_KEYS.includes(value as InventoryTabKey)) {
    return value as InventoryTabKey;
  }
  return "retail";
};

const buildSearch = (location: ReturnType<typeof useLocation>, tab: InventoryTabKey) => {
  const params = new URLSearchParams(location.search);
  params.set("tab", tab);
  return `?${params.toString()}`;
};

const InventoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "retail"]);

  const activeTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return resolveTabKey(params.get("tab"));
  }, [location.search]);

  const handleTabChange = (next: string) => {
    const resolved = resolveTabKey(next);
    navigate(
      {
        pathname: location.pathname,
        search: buildSearch(location, resolved),
      },
      { replace: true },
    );
  };

  const tabLabels: Record<InventoryTabKey, string> = {
    retail: t("retail.inventory.tabs.retail", { defaultValue: "Retail" }),
    kitchen: t("retail.inventory.tabs.kitchen", { defaultValue: "Kitchen" }),
  };

  return (
    <div className="flex flex-col gap-6">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <TabsList className="inline-flex h-10 items-center gap-2 rounded-full bg-slate-100/70 p-1 text-sm dark:bg-slate-900/40">
            {TAB_KEYS.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className={cn(
                  "rounded-full px-4 py-2 font-medium transition",
                  "data-[state=active]:bg-white data-[state=active]:text-emerald-600 dark:data-[state=active]:bg-slate-800 dark:data-[state=active]:text-emerald-300",
                  "data-[state=inactive]:text-slate-500 dark:data-[state=inactive]:text-slate-400",
                )}
              >
                {tabLabels[tab]}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="retail" className="mt-0">
          <RetailInventoryView />
        </TabsContent>

        <TabsContent value="kitchen" className="mt-0">
          <KitchenInventoryPage />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InventoryPage;
